// Adaptation du stockage chiffré de ScanGabon/apps/capture/src/stockage/base.ts
// et des primitives securite/chiffrement.ts. Espaces propres à CollectivitésConnect.
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import {
  chiffrerOctets,
  creerCleDonnees,
  dechiffrerOctets,
  deverrouillerCleDonnees,
  type Chiffre,
  type CleEnveloppee,
} from './chiffrement';
import { lireFichier, tamponIsole } from './octets';
import type { Coins } from './types';
import { coinsLisibles, quadrilatereValide } from './geometrie';

interface Meta {
  espace: string;
  cle: CleEnveloppee;
  echecs: number;
  bloqueJusqua: number;
}
interface Brouillon {
  id: string;
  espace: string;
  original: Chiffre;
  description: Chiffre;
}
interface BaseCapture extends DBSchema {
  espaces: { key: string; value: Meta };
  brouillons: { key: string; value: Brouillon; indexes: { espace: string } };
}
export function ouvrirBaseCapture() {
  return openDB<BaseCapture>('cc-capture-documents', 1, {
    upgrade(db) {
      db.createObjectStore('espaces', { keyPath: 'espace' });
      db.createObjectStore('brouillons', { keyPath: 'id' }).createIndex('espace', 'espace');
    },
  });
}
export async function captureConfiguree(espace: string) {
  const base = await ouvrirBaseCapture();
  try {
    return Boolean(await base.get('espaces', espace));
  } finally {
    base.close();
  }
}

export async function identifiantEspace(compte: string, appartenance: string): Promise<string> {
  if (!compte || !appartenance) throw new Error('Compte et espace de travail requis');
  const h = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(JSON.stringify([compte, appartenance])),
  );
  return Array.from(new Uint8Array(h), (n) => n.toString(16).padStart(2, '0')).join('');
}

export class BrouillonsCapture {
  constructor(
    private base: IDBPDatabase<BaseCapture>,
    readonly espace: string,
    private cle: CryptoKey | null,
  ) {}
  fermer() {
    this.cle = null;
    this.base.close();
  }
  private cleOuErreur() {
    if (!this.cle) throw new Error('Brouillons verrouillés : saisissez votre code local');
    return this.cle;
  }
  private id(usage: string) {
    if (!usage || usage.length > 300) throw new Error('Référence de pièce invalide');
    return `${this.espace}:${usage}`;
  }
  async enregistrer(usage: string, fichier: File, coins: Coins) {
    if (!coinsLisibles(coins) || !quadrilatereValide(coins)) throw new Error('Coins invalides');
    if (!fichier.size || fichier.size > 25 * 1024 * 1024) throw new Error('Photo limitée à 25 Mo');
    const id = this.id(usage);
    const cle = this.cleOuErreur();
    const original = await chiffrerOctets(cle, await lireFichier(fichier), `${id}:original`);
    const description = await chiffrerOctets(
      cle,
      new TextEncoder().encode(JSON.stringify({ nom: fichier.name, type: fichier.type, coins })),
      `${id}:description`,
    );
    this.cleOuErreur();
    await this.base.put('brouillons', { id, espace: this.espace, original, description });
  }
  async lire(usage: string): Promise<{ fichier: File; coins: Coins } | null> {
    const cle = this.cleOuErreur();
    const id = this.id(usage);
    const b = await this.base.get('brouillons', id);
    if (!b) return null;
    if (b.espace !== this.espace) throw new Error('Brouillon d’un autre espace');
    const meta = JSON.parse(
      new TextDecoder().decode(await dechiffrerOctets(cle, b.description, `${id}:description`)),
    ) as { nom: string; type: string; coins: Coins };
    if (
      typeof meta.nom !== 'string' ||
      typeof meta.type !== 'string' ||
      !coinsLisibles(meta.coins) ||
      !quadrilatereValide(meta.coins)
    )
      throw new Error('Brouillon invalide');
    const original = await dechiffrerOctets(cle, b.original, `${id}:original`);
    this.cleOuErreur();
    return {
      fichier: new File([tamponIsole(original)], meta.nom, { type: meta.type }),
      coins: meta.coins,
    };
  }
  async effacer(usage: string) {
    this.cleOuErreur();
    await this.base.delete('brouillons', this.id(usage));
  }
  async listerAutres(usageCourant: string): Promise<{ usage: string; nom: string }[]> {
    const cle = this.cleOuErreur();
    const lignes = await this.base.getAllFromIndex('brouillons', 'espace', this.espace);
    const resultat = [];
    for (const ligne of lignes) {
      if (ligne.id === this.id(usageCourant)) continue;
      const description = JSON.parse(
        new TextDecoder().decode(
          await dechiffrerOctets(cle, ligne.description, `${ligne.id}:description`),
        ),
      ) as { nom: string };
      if (typeof description.nom !== 'string') throw new Error('Brouillon invalide');
      resultat.push({ usage: ligne.id.slice(this.espace.length + 1), nom: description.nom });
    }
    this.cleOuErreur();
    return resultat;
  }
}

export async function ouvrirBrouillons(espace: string, code: string): Promise<BrouillonsCapture> {
  if (!/^[a-f0-9]{64}$/.test(espace) || !/^\d{6}$/.test(code))
    throw new Error('Un code local à six chiffres est requis');
  const base = await ouvrirBaseCapture();
  try {
    let meta = await base.get('espaces', espace);
    if (!meta) {
      const nouvelle = await creerCleDonnees(code);
      const tx = base.transaction('espaces', 'readwrite');
      meta = await tx.store.get(espace);
      if (!meta) {
        meta = { espace, cle: nouvelle.enveloppee, echecs: 0, bloqueJusqua: 0 };
        await tx.store.add(meta);
      }
      await tx.done;
    }
    if (meta.bloqueJusqua > Date.now())
      throw new Error('Trop d’essais : réessayez dans quinze minutes');
    // Réserver l'essai avant la dérivation, y compris entre plusieurs onglets.
    const tx = base.transaction('espaces', 'readwrite');
    const actuelle = (await tx.store.get(espace))!;
    if (actuelle.bloqueJusqua > Date.now()) {
      await tx.done;
      throw new Error('Trop d’essais : réessayez dans quinze minutes');
    }
    const echecs = actuelle.bloqueJusqua ? 1 : actuelle.echecs + 1;
    await tx.store.put({
      ...actuelle,
      echecs,
      bloqueJusqua: echecs >= 5 ? Date.now() + 15 * 60000 : 0,
    });
    await tx.done;
    const cle = await deverrouillerCleDonnees(code, meta.cle);
    const succes = base.transaction('espaces', 'readwrite');
    const recente = (await succes.store.get(espace))!;
    await succes.store.put({ ...recente, echecs: 0, bloqueJusqua: 0 });
    await succes.done;
    return new BrouillonsCapture(base, espace, cle);
  } catch (erreur) {
    base.close();
    throw erreur;
  }
}
